/**
 * F-car-logos — logo da marca do veículo (como no eDSP), a partir do
 * `vehicle_make_snapshot` ("MINI Cooper", "ALFA ROMEO Giulia"…). Assets em
 * /public/car-logos (thumbs ~5 KB, dataset MIT filippofilip95/car-logos-dataset).
 * Marca desconhecida/vazia → medalhão com silhueta genérica de carro, pra
 * linha não ficar "sem logo" e desalinhada nas listas.
 */

const KNOWN = new Set([
  "abarth",
  "alfa-romeo",
  "alpine",
  "audi",
  "bmw",
  "byd",
  "chevrolet",
  "chrysler",
  "citroen",
  "cupra",
  "dacia",
  "daewoo",
  "daihatsu",
  "dodge",
  "ds",
  "fiat",
  "ford",
  "genesis",
  "honda",
  "hyundai",
  "infiniti",
  "isuzu",
  "jaguar",
  "jeep",
  "kia",
  "lada",
  "lancia",
  "land-rover",
  "lexus",
  "mazda",
  "mercedes-benz",
  "mg",
  "mini",
  "mitsubishi",
  "nissan",
  "opel",
  "peugeot",
  "polestar",
  "porsche",
  "renault",
  "saab",
  "seat",
  "skoda",
  "smart",
  "ssangyong",
  "subaru",
  "suzuki",
  "tesla",
  "toyota",
  "volkswagen",
  "volvo",
]);

const ALIASES: Record<string, string> = {
  mercedes: "mercedes-benz",
  vw: "volkswagen",
  landrover: "land-rover",
  "alfa-roméo": "alfa-romeo",
  citroën: "citroen",
  škoda: "skoda",
};

/** URL do logo da marca, ou null quando não reconhecida. */
export function carLogoSrc(makeModel: string | null | undefined): string | null {
  if (!makeModel) return null;
  const norm = makeModel.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const words = norm.split(/\s+/).filter(Boolean);
  const one = (words[0] ?? "").replace(/[^a-z0-9-]/g, "");
  const two = words
    .slice(0, 2)
    .join("-")
    .replace(/[^a-z0-9-]/g, "");
  for (const cand of [two, ALIASES[two] ?? "", one, ALIASES[one] ?? ""]) {
    if (cand && KNOWN.has(cand)) return `/car-logos/${cand}.png`;
  }
  return null;
}

/**
 * Logo em medalhão branco SÓLIDO com borda sutil — feedback do CEO
 * (2026-09-11): pequeno demais e "sumindo" no fundo branco; agora maior,
 * redondo e com anel para destacar do fundo em ambos os temas.
 */
export function CarLogo({
  make,
  size = 30,
  className = "",
}: {
  make: string | null | undefined;
  size?: number;
  className?: string;
}) {
  const src = carLogoSrc(make);
  const pad = Math.max(3, Math.round(size * 0.12));
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-neutral-400/40 shadow-sm ${className}`}
      style={{ width: size + pad * 2, height: size + pad * 2 }}
    >
      {src ? (
        <img
          src={src}
          width={size}
          height={size}
          alt=""
          loading="lazy"
          className="object-contain"
        />
      ) : (
        // Silhueta genérica (cor fixa: o medalhão é sempre branco nos 2 temas)
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="#9CA3AF"
          aria-hidden="true"
          style={{ padding: Math.round(size * 0.12) }}
        >
          <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11h.5a1.5 1.5 0 0 1 1.5 1.5V16a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H9a2 2 0 1 1-4 0H4a1 1 0 0 1-1-1v-3.5A1.5 1.5 0 0 1 4.5 11H5zm2.1 0h9.8l-1.1-3.3a.5.5 0 0 0-.5-.35H8.7a.5.5 0 0 0-.5.35L7.1 11z" />
        </svg>
      )}
    </span>
  );
}
